import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import PageHeader from "../../../components/layout/PageHeader";
import Pagination from "../../../components/ui/Pagination";
import {
  Button,
  EmptyStateProps,
  failedEmptyState,
  searchEmptyState,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { useSubscriptionGate } from "../../billing/hooks/useSubscriptionGate";
// The mark over the first-run state, for a workspace that has never uploaded
// a dish — see `EmptyState`'s `welcome` variant.
import { ReactComponent as FirstDishMark } from "../../../assets/svg/No data images/Dishes.svg";
import BulkDishUploadModal from "../components/BulkDishUploadModal";
import DishImportReportModal from "../components/DishImportReportModal";
import DishesTable from "../components/DishesTable";
import { OPEN_BULK_UPLOAD } from "../../../routes/paths";
import { useDishes } from "../hooks/useDishes";
import { OWN_DISH_COUNT_QUERY_KEY } from "../hooks/useOwnDishCount";
import { DEFAULT_PAGE_SIZE } from "../services/dishesService";
import {
  downloadDishTemplate,
  importDishes,
  saveDishImportErrors,
} from "../services/dishImport";
import { ApiError } from "../../../lib/http";
import type { DishImportResult } from "../types/dishes.types";

/** What one page of the table holds, and what the endpoint is asked for. */
const DISHES_PER_PAGE = DEFAULT_PAGE_SIZE;

const DishesPage: React.FC = () => {
  const toast = useToast();
  const getToken = useTokenGetter();

  // A dish sheet is written into the workspace, so with no plan running both
  // ways to Upload are off. The dishes already in stay readable and searchable
  // — what was uploaded during the trial does not stop being theirs. See
  // `useSubscriptionGate`.
  const gate = useSubscriptionGate();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [uploading, setUploading] = useState(false);

  // Somebody sent here to upload rather than to read — see `OPEN_BULK_UPLOAD`.
  // Once per arrival: the flag is taken off the history entry straight away,
  // so a Back to this page opens the list rather than the modal again.
  const location = useLocation();
  const navigate = useNavigate();
  const asked = !!(location.state as Record<string, unknown> | null)?.[
    OPEN_BULK_UPLOAD
  ];
  const honoured = useRef(false);
  const here = location.pathname + location.search;
  useEffect(() => {
    if (!asked || honoured.current) return;
    honoured.current = true;
    navigate(here, { replace: true, state: null });
    setUploading(true);
  }, [asked, here, navigate]);

  // What the last upload did, and the workbook it did it to. Held together
  // because the report is titled with the file name, and a report over the
  // wrong name is worse than none.
  const [report, setReport] = useState<{
    fileName: string;
    result: DishImportResult;
  } | null>(null);

  // The page is the server's; the search narrows the page in hand, because the
  // endpoint has no search param yet. See `useDishes`, which says so in full.
  const queryClient = useQueryClient();
  const {
    dishes,
    count,
    searchScope,
    pageSize,
    status: loadStatus,
    error,
    reload,
  } = useDishes({ search, page, pageSize: DISHES_PER_PAGE });

  // A list that lands quickly goes straight from blank to filled: the table's
  // placeholders are only reached once the fetch has visibly stalled.
  const phase = useLoadPhase(loadStatus === "loading");

  // The server's count, not the rows on screen — the search does not change
  // how many pages there are, since it never reaches past the one in hand.
  const totalPages = Math.max(1, Math.ceil(count / DISHES_PER_PAGE));
  const currentPage = Math.min(page, totalPages);

  // An upload that shrank the list past the page in hand lands on the last one,
  // rather than leaving the screen asking for a page that is no longer there.
  useEffect(() => {
    if (loadStatus === "ready" && page > totalPages) setPage(totalPages);
  }, [loadStatus, page, totalPages]);

  // A failure comes before emptiness: an empty table under "no dishes" would
  // read as an answer, when nothing was ever heard back. Where the server said
  // why, its words replace the generic reassurance — they are more specific
  // than anything written here could be.
  const emptyState = (): EmptyStateProps => {
    if (loadStatus === "failed") {
      const state = failedEmptyState("We could not load your dishes");
      return error ? { ...state, description: error } : state;
    }

    if (searchScope === "page") {
      const state = searchEmptyState("dishes");
      // The standard line would tell them to clear the search to see the whole
      // list, which is true — but it would not say that the search only looked
      // at this page, and a reader whose dish is on page three deserves to
      // know that before they conclude it is missing.
      return {
        ...state,
        description:
          totalPages > 1
            ? "The search looks through the page you are on. Check the spelling, or clear it and turn the page."
            : state.description,
      };
    }

    return {
      variant: "welcome",
      // Sized here rather than left at the file's own 56px, the same way the
      // clients and enquiries marks are — the class is what keeps the three
      // matched on screen.
      illustration: <FirstDishMark aria-hidden className="w-11 h-11" />,
      title: "Upload multiple dishes at once",
      description: "Save time by adding your dishes in bulk using our template.",
      // Steps rather than benefits, which is the one thing this screen's list
      // does differently to the others: there is no way to add a dish one at a
      // time, so a reader who has never seen the template needs the order of
      // the three moves before the button is worth pressing.
      highlights: {
        title: "How it works",
        items: [
          "Use our Excel template and fill in your dish details.",
          "Enter dish name, calories, carbs, protein, fat, and other required details.",
          "Upload the completed template and we'll add your dishes to your library.",
        ],
      },
      action: (
        <Button
          size="md"
          startIcon={<Plus className="w-4 h-4" />}
          onClick={() => setUploading(true)}
          {...gate.props("upload dishes")}
        >
          Bulk upload
        </Button>
      ),
    };
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    // The search reaches no further than the page it is on, so turning back to
    // the first one is the only honest place to run it from.
    setPage(1);
  };

  const failureMessage = (cause: unknown) =>
    cause instanceof ApiError
      ? cause.message
      : "We could not reach the server. Check your connection and try again.";

  /**
   * Fetches the blank workbook. The dialog stays up either way — the template
   * is a step on the way to the upload, not the thing the dialog is for — so a
   * failure is said in a toast rather than in the panel, where the message
   * beside the drop zone is reserved for what is wrong with the file.
   */
  const handleDownloadTemplate = async () => {
    try {
      await downloadDishTemplate(getToken);
    } catch (cause) {
      toast.error("Could not download the template", failureMessage(cause));
      console.error("dish template download failed:", cause);
    }
  };

  /**
   * Sends the filled sheet and says what became of it.
   *
   * Three endings, and they are genuinely different things. The request
   * failing is the only one that keeps the upload dialog up — the sheet never
   * got there, and the reason belongs beside the file it is about, which is
   * what the rethrow leaves it to do.
   *
   * A sheet that arrived has landed some rows and been refused others, and
   * either way the upload dialog has finished its job. Every row in means a
   * toast and nothing more; a single row refused means the report, because
   * "8 rows failed" said in a toast that disappears after five seconds is not
   * something anybody can act on.
   *
   * The list is re-read rather than added to from `foods`: what the server
   * called each dish, and which page it now falls on, is the server's answer.
   */
  const handleImport = async (file: File) => {
    let result: DishImportResult;

    try {
      result = await importDishes(getToken, file);
    } catch (cause) {
      console.error("dish import failed:", cause);
      throw new Error(failureMessage(cause));
    }

    setUploading(false);

    // Only where something actually landed. A sheet refused whole has left the
    // table exactly as it was, and a refetch would blank the rows and bring
    // back the same ones for nothing.
    if (result.succeeded > 0) {
      setPage(1);
      reload();
      // The count too: a first upload is what lets the Dish search be kept to
      // your own dishes, and whoever is holding that answer should hear.
      void queryClient.invalidateQueries({ queryKey: OWN_DISH_COUNT_QUERY_KEY });
    }

    if (result.failed > 0) {
      setReport({ fileName: file.name, result });
      return;
    }

    toast.success(
      "Dishes uploaded",
      `${result.succeeded} ${result.succeeded === 1 ? "dish" : "dishes"} from ${
        file.name
      } went in.`
    );
  };

  /**
   * Saves the refused rows as a workbook to fix and send again.
   *
   * The bytes came over with the report, so there is no call here and nothing
   * to fail asynchronously — a throw is the decode giving up, and the dialog
   * catches it and says so in place of its instruction line.
   */
  const handleDownloadErrors = () => {
    if (!report?.result.errorSheet) return;
    saveDishImportErrors(report.result.errorSheet);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
      <PageHeader title="Dishes" />

      <section className="mt-5 flex-1 min-h-0 flex flex-col overflow-hidden rounded-md border border-line bg-white">
        <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-6">
          {/* No count until the list is in: a "(0)" beside a heading that is
              still loading reads as an answer rather than as a blank. */}
          <h2 className="text-[18px] 3xl:text-[20px] font-medium text-[#111C2D]">
            Total dishes{loadStatus === "ready" && ` (${count})`}
          </h2>

          <div className="flex items-center gap-3">
            <div className="relative w-full sm:w-[260px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" />
              <input
                type="search"
                value={search}
                onChange={(event) => handleSearchChange(event.target.value)}
                placeholder="Search"
                // Named for what it actually does. The endpoint takes no
                // search param, so this looks through the page on screen, and
                // the label should not promise the whole list.
                aria-label="Search the dishes on this page"
                className="w-full rounded-md border border-line py-2 pl-9 pr-3 text-[14px] text-ink placeholder:text-ink-subtle focus:outline-none focus:border-brand"
              />
            </div>
            <Button
              size="sm"
              startIcon={<Plus className="w-4 h-4" />}
              onClick={() => setUploading(true)}
              {...gate.props("upload dishes")}
            >
              Bulk upload
            </Button>
          </div>
        </div>

        {/* Said as soon as the request goes out, whether or not anything is
            drawn for it: this line is not seen, so it costs nothing to be
            early, and the skeleton rows it stands for are hidden from screen
            readers — they are the shape of an answer rather than the answer. */}
        {loadStatus === "loading" && (
          <p role="status" className="sr-only">
            Loading dishes…
          </p>
        )}

        <DishesTable
          dishes={dishes}
          // The dish's place in the whole list, not on the screen — so page two
          // opens at 11. Counted off the page the server was asked for and the
          // rows it sent back, which is why the hook reports the second.
          startIndex={(currentPage - 1) * DISHES_PER_PAGE + 1}
          phase={phase}
          empty={emptyState()}
        />

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setPage}
          // What the page holds, not what the search left of it: the footer
          // counts the page, and the rows a search hid are still on it.
          resultCount={pageSize}
        />
      </section>

      <BulkDishUploadModal
        open={uploading}
        onClose={() => setUploading(false)}
        onDownloadTemplate={handleDownloadTemplate}
        onImport={handleImport}
      />

      {/* Kept mounted across the close so the panel is not blanked as it
          leaves — the same reason every other dialog on the app is handed its
          record rather than being rendered only while it holds one. */}
      <DishImportReportModal
        open={report !== null}
        fileName={report?.fileName ?? ""}
        result={report?.result ?? null}
        onClose={() => setReport(null)}
        onDownloadErrors={handleDownloadErrors}
      />
    </div>
  );
};

export default DishesPage;
